/**
 * ACCourseService
 * Service for managing courses with view endpoints and dashboard statistics
 */
import { CrudService } from '../base/CrudService';
/**
 * Service for course CRUD operations, list/detail views and dashboard statistics
 */
export class ACCourseService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? ACCourseService.DEFAULT_SERVICE_PATH,
            basePath: ACCourseService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /**
     * Get all courses as enriched model views (status, sessionCount, upcoming dates, etc.).
     * GET api/courses/views
     * @returns Array of course model views
     */
    async getAllViews() {
        const response = await this.getAllViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get all courses as enriched model views with full response.
     * GET api/courses/views
     * @returns ResponseData with array of course model views
     */
    getAllViewsRaw() {
        const url = this.buildUrl('views');
        return this.http.get(url);
    }
    /**
     * Get all active courses
     * @returns Array of active courses
     */
    async getActive() {
        const response = await this.getActiveRaw();
        return response.data ?? [];
    }
    /**
     * Get all active courses with full response
     * @returns ResponseData with array of active courses
     */
    getActiveRaw() {
        const url = this.buildUrl('active');
        return this.http.get(url);
    }
    /**
     * Get course view by ID (enriched status + upcoming dates + session templates)
     * @param id - The course ID
     * @returns Course model view
     */
    async getView(id) {
        const response = await this.getViewRaw(id);
        if (!response.data) {
            throw new Error(`Course view with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get course view by ID with full response
     * @param id - The course ID
     * @returns ResponseData with course model view
     */
    getViewRaw(id) {
        const url = this.buildUrl(`view/${id}`);
        return this.http.get(url);
    }
    /**
     * Get the course list view (filtered)
     * @param filter - Optional filter parameters
     * @returns Array of course model views
     */
    async listView(filter) {
        const response = await this.listViewRaw(filter);
        return response.data ?? [];
    }
    /**
     * Get the course list view (filtered) with full response
     * @param filter - Optional filter parameters
     * @returns ResponseData with array of course model views
     */
    listViewRaw(filter) {
        const url = this.buildUrl('list-view');
        return this.http.post(url, filter ?? {});
    }
    /**
     * Get course dashboard statistics
     * @returns Dashboard statistics for the course admin home
     */
    async dashboardStats() {
        const response = await this.dashboardStatsRaw();
        if (!response.data) {
            throw new Error('Failed to retrieve course dashboard stats');
        }
        return response.data;
    }
    /**
     * Get course dashboard statistics with full response
     * @returns ResponseData with dashboard statistics
     */
    dashboardStatsRaw() {
        const url = this.buildUrl('dashboard-stats');
        return this.http.get(url);
    }
    /**
     * Change only the course's status.
     * PATCH api/courses/{id}/change-status/{newStatus}
     */
    async changeStatus(id, newStatus) {
        const response = await this.changeStatusRaw(id, newStatus);
        if (!response.data)
            throw new Error(`Failed to change status for course ${id}`);
        return response.data;
    }
    changeStatusRaw(id, newStatus) {
        return this.http.patch(this.buildUrl(`${id}/change-status/${newStatus}`));
    }
    // ---------------------------------------------------------------------------
    // Sessions (course session templates / curriculum)
    // ---------------------------------------------------------------------------
    /** List a course's session templates (curriculum). */
    async getSessions(courseId) {
        const response = await this.getSessionsRaw(courseId);
        return response.data ?? [];
    }
    getSessionsRaw(courseId) {
        const url = this.buildUrl(`${courseId}/sessions`);
        return this.http.get(url);
    }
    /** Add a session template to a course. */
    async addSession(courseId, body) {
        const response = await this.addSessionRaw(courseId, body);
        if (!response.data) {
            throw new Error(`Failed to add session to course ${courseId}`);
        }
        return response.data;
    }
    addSessionRaw(courseId, body) {
        const url = this.buildUrl(`${courseId}/sessions`);
        return this.http.post(url, body);
    }
    /** Update a course's session template. */
    async updateSession(courseId, sessionId, body) {
        const response = await this.updateSessionRaw(courseId, sessionId, body);
        if (!response.data) {
            throw new Error(`Failed to update session ${sessionId} for course ${courseId}`);
        }
        return response.data;
    }
    updateSessionRaw(courseId, sessionId, body) {
        const url = this.buildUrl(`${courseId}/sessions/${sessionId}`);
        return this.http.put(url, body);
    }
    /** Remove a course's session template. */
    async removeSession(courseId, sessionId) {
        const response = await this.removeSessionRaw(courseId, sessionId);
        return response.success;
    }
    removeSessionRaw(courseId, sessionId) {
        const url = this.buildUrl(`${courseId}/sessions/${sessionId}`);
        return this.http.delete(url);
    }
    // ---------------------------------------------------------------------------
    // Dates (scheduled intakes / cohorts)
    // ---------------------------------------------------------------------------
    /** List a course's scheduled intake dates. */
    async getDates(courseId) {
        const response = await this.getDatesRaw(courseId);
        return response.data ?? [];
    }
    getDatesRaw(courseId) {
        const url = this.buildUrl(`${courseId}/dates`);
        return this.http.get(url);
    }
    /** Add a scheduled intake date to a course. */
    async addDate(courseId, body) {
        const response = await this.addDateRaw(courseId, body);
        if (!response.data) {
            throw new Error(`Failed to add date to course ${courseId}`);
        }
        return response.data;
    }
    addDateRaw(courseId, body) {
        const url = this.buildUrl(`${courseId}/dates`);
        return this.http.post(url, body);
    }
    /** Update a course's intake date. */
    async updateDate(courseId, dateId, body) {
        const response = await this.updateDateRaw(courseId, dateId, body);
        if (!response.data) {
            throw new Error(`Failed to update date ${dateId} for course ${courseId}`);
        }
        return response.data;
    }
    updateDateRaw(courseId, dateId, body) {
        const url = this.buildUrl(`${courseId}/dates/${dateId}`);
        return this.http.put(url, body);
    }
    /** Remove a course's intake date. */
    async removeDate(courseId, dateId) {
        const response = await this.removeDateRaw(courseId, dateId);
        return response.success;
    }
    removeDateRaw(courseId, dateId) {
        const url = this.buildUrl(`${courseId}/dates/${dateId}`);
        return this.http.delete(url);
    }
    // ---------------------------------------------------------------------------
    // Facilitators (FacilitatorCourse junction, course side)
    // ---------------------------------------------------------------------------
    /** List the facilitators assigned to a course. */
    async getFacilitators(courseId) {
        const response = await this.getFacilitatorsRaw(courseId);
        return response.data ?? [];
    }
    getFacilitatorsRaw(courseId) {
        const url = this.buildUrl(`${courseId}/facilitators`);
        return this.http.get(url);
    }
    /** Assign a facilitator to a course. */
    async addFacilitator(courseId, body) {
        const response = await this.addFacilitatorRaw(courseId, body);
        if (!response.data) {
            throw new Error(`Failed to assign facilitator to course ${courseId}`);
        }
        return response.data;
    }
    addFacilitatorRaw(courseId, body) {
        const url = this.buildUrl(`${courseId}/facilitators`);
        return this.http.post(url, body);
    }
    /** Update a course's facilitator assignment. */
    async updateFacilitator(courseId, facilitatorCourseId, body) {
        const response = await this.updateFacilitatorRaw(courseId, facilitatorCourseId, body);
        if (!response.data) {
            throw new Error(`Failed to update facilitator ${facilitatorCourseId} for course ${courseId}`);
        }
        return response.data;
    }
    updateFacilitatorRaw(courseId, facilitatorCourseId, body) {
        const url = this.buildUrl(`${courseId}/facilitators/${facilitatorCourseId}`);
        return this.http.put(url, body);
    }
    /** Remove a course's facilitator assignment. */
    async removeFacilitator(courseId, facilitatorCourseId) {
        const response = await this.removeFacilitatorRaw(courseId, facilitatorCourseId);
        return response.success;
    }
    removeFacilitatorRaw(courseId, facilitatorCourseId) {
        const url = this.buildUrl(`${courseId}/facilitators/${facilitatorCourseId}`);
        return this.http.delete(url);
    }
    // ---------------------------------------------------------------------------
    // Files (course resources / handouts)
    // ---------------------------------------------------------------------------
    /** List a course's resource files. */
    async getFiles(courseId) {
        const response = await this.getFilesRaw(courseId);
        return response.data ?? [];
    }
    getFilesRaw(courseId) {
        const url = this.buildUrl(`${courseId}/files`);
        return this.http.get(url);
    }
    /** Add a resource file to a course. */
    async addFile(courseId, body) {
        const response = await this.addFileRaw(courseId, body);
        if (!response.data) {
            throw new Error(`Failed to add file to course ${courseId}`);
        }
        return response.data;
    }
    addFileRaw(courseId, body) {
        const url = this.buildUrl(`${courseId}/files`);
        return this.http.post(url, body);
    }
    /** Update a course's resource file. */
    async updateFile(courseId, fileId, body) {
        const response = await this.updateFileRaw(courseId, fileId, body);
        if (!response.data) {
            throw new Error(`Failed to update file ${fileId} for course ${courseId}`);
        }
        return response.data;
    }
    updateFileRaw(courseId, fileId, body) {
        const url = this.buildUrl(`${courseId}/files/${fileId}`);
        return this.http.put(url, body);
    }
    /** Remove a course's resource file. */
    async removeFile(courseId, fileId) {
        const response = await this.removeFileRaw(courseId, fileId);
        return response.success;
    }
    removeFileRaw(courseId, fileId) {
        const url = this.buildUrl(`${courseId}/files/${fileId}`);
        return this.http.delete(url);
    }
}
ACCourseService.DEFAULT_SERVICE_PATH = 'ac-courses';
ACCourseService.BASE_PATH = 'api/courses';
//# sourceMappingURL=ACCourseService.js.map